// src/pages/ActivitiesBuilder/components/ActivityDetailDialog.tsx
// src/pages/ActivitiesBuilder/components/ActivityDetailDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Rating,
  IconButton,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';

import { Activity } from '../../../contexts/ActivityContext';
import { Trip } from '../../../contexts/ItineraryContext';
import { useItinerary } from '../../../contexts/ItineraryContext';

interface ActivityDetailDialogProps {
  open: boolean;
  onClose: () => void;
  activity: Activity;
  onSave: (activity: Activity) => Promise<void>;
  trip: Trip;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-detail-tabpanel-${index}`}
      aria-labelledby={`activity-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `activity-detail-tab-${index}`,
    'aria-controls': `activity-detail-tabpanel-${index}`,
  };
}

const ActivityDetailDialog: React.FC<ActivityDetailDialogProps> = ({
  open,
  onClose,
  activity,
  onSave,
  trip
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [isAddingToItinerary, setIsAddingToItinerary] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(0);
  
  // Get itinerary context - access through trip prop instead to avoid dependency on context
  // const { itinerary, addToItinerary } = useItinerary();
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleAddToItinerary = async () => {
    setIsAddingToItinerary(true);
    try {
      // Mock implementation since we're not using the context directly

      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate delay
      onClose();
    } catch (error) {
      console.error('Error adding to itinerary:', error);
    } finally {
      setIsAddingToItinerary(false);
    }
  };
  
  const generateTimeSlots = () => {
    return [
      '07:00 AM',
      '09:00 AM',
      '12:00 PM',
      '03:00 PM',
      '06:00 PM',
      '08:00 PM'
    ];
  };
  
  // Generate mock days for the trip
  const generateDays = () => {
    if (!trip.startDate || !trip.endDate) return [];
    
    const days = [];
    let currentDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);
    
    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };
  
  const days = generateDays();
  const timeslots = generateTimeSlots();
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{activity.title}</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {/* Activity Image */}
          {activity.imageUrl && (
            <Grid item xs={12} md={5} sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={activity.imageUrl}
                alt={activity.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  maxHeight: { xs: 200, md: '100%' },
                  objectFit: 'cover'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  borderBottomLeftRadius: 8
                }}
              >
                <IconButton
                  color={activity.isSaved ? 'primary' : 'default'}
                  onClick={() => onSave(activity)}
                >
                  {activity.isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </IconButton>
              </Box>
            </Grid>
          )}
          
          {/* Activity Details */}
          <Grid item xs={12} md={activity.imageUrl ? 7 : 12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="activity detail tabs"
                variant="fullWidth"
              >
                <Tab label="Details" {...a11yProps(0)} />
                <Tab label="Add to Itinerary" {...a11yProps(1)} />
              </Tabs>
            </Box>
            
            {/* Details Tab */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="body1" paragraph>
                {activity.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">
                      <strong>Location:</strong> {activity.location}
                    </Typography>
                  </Box>
                  
                  {activity.address && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {activity.address}
                      </Typography>
                    </Box>
                  )}
                  
                  {activity.duration && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        <strong>Duration:</strong> {activity.duration}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  {activity.website && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WebsiteIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" component="a" href={activity.website} target="_blank" rel="noopener noreferrer">
                        Visit Website
                      </Typography>
                    </Box>
                  )}
                  
                  {activity.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {activity.phone}
                      </Typography>
                    </Box>
                  )}
                  
                  {activity.cost !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Cost:</strong> ${activity.cost.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
              
              {activity.rating && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Rating:</strong>
                  </Typography>
                  <Rating value={activity.rating} precision={0.5} readOnly />
                </Box>
              )}
              
              {activity.tags && activity.tags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Tags:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {activity.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </TabPanel>
            
            {/* Add to Itinerary Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Add to Your Itinerary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select which day and time slot you want to add this activity to.
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="day-select-label">Day</InputLabel>
                    <Select
                      labelId="day-select-label"
                      id="day-select"
                      value={selectedDay}
                      label="Day"
                      onChange={(e) => setSelectedDay(e.target.value as number)}
                    >
                      {days.map((day, index) => (
                        <MenuItem key={index} value={index}>
                          Day {index + 1} ({day.toLocaleDateString()})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="timeslot-select-label">Time Slot</InputLabel>
                    <Select
                      labelId="timeslot-select-label"
                      id="timeslot-select"
                      value={selectedTimeSlot}
                      label="Time Slot"
                      onChange={(e) => setSelectedTimeSlot(e.target.value as number)}
                    >
                      {timeslots.map((slot, index) => (
                        <MenuItem key={index} value={index}>
                          {slot}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddToItinerary}
                  disabled={isAddingToItinerary}
                  startIcon={isAddingToItinerary ? <CircularProgress size={20} /> : <CalendarIcon />}
                >
                  {isAddingToItinerary ? 'Adding...' : 'Add to Itinerary'}
                </Button>
              </Box>
            </TabPanel>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!activity.isSaved ? (
          <Button 
            variant="outlined" 
            startIcon={<BookmarkIcon />}
            color="primary"
            onClick={() => onSave(activity)}
          >
            Save Activity
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<BookmarkBorderIcon />}
            color="secondary"
            onClick={() => onSave(activity)}
          >
            Unsave Activity
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ActivityDetailDialog;